import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, Download, RefreshCw, Filter, BarChart3, Calendar as CalendarIcon, X } from "lucide-react";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  open: "bg-[#E6EFEC] text-[#0B4636] border-[#0B4636]/30",
  issued: "bg-[#EAF0FB] text-[#102968] border-[#102968]/30",
  closing: "bg-[#FFF4DB] text-[#8B5E00] border-[#8B5E00]/30",
  closed: "bg-[#E5E5DF] text-[#0A0A0A] border-[#0A0A0A]/30",
  matured: "bg-[#102968] text-white border-[#102968]",
};

const PATH_LABEL = {
  spv_trust: "SPV Trust",
  discount_sale: "Outright Sale",
  financing: "Collateral Loan",
};

const fmt$ = (n) => (typeof n === "number" ? `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : "—");
const fmtTs = (s) => {
  if (!s) return "—";
  try { return new Date(s).toLocaleString(); } catch { return s; }
};

export default function AuditDashboard() {
  const [summary, setSummary] = useState(null);
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: "", path: "", since: "", until: "" });
  const [drillDown, setDrillDown] = useState(null);

  // When a row is clicked, fetch its event_log
  useEffect(() => {
    if (!drillDown?.tx?.id || drillDown.log !== null) return;
    axios
      .get(`${API}/audit/transactions/${drillDown.tx.id}/event_log`)
      .then((r) => setDrillDown((prev) => ({ ...prev, log: r.data, loading: false })))
      .catch((e) => setDrillDown((prev) => ({ ...prev, log: { error: e.message, entries: [] }, loading: false })));
  }, [drillDown?.tx?.id, drillDown?.log]);

  const refresh = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && params.append(k, v));
    try {
      const [s, t] = await Promise.all([
        axios.get(`${API}/audit/summary`),
        axios.get(`${API}/audit/transactions?${params.toString()}`),
      ]);
      setSummary(s.data);
      setTxns(t.data.transactions || []);
    } catch (e) { /* ignore */ }
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(); }, []);

  const downloadCSV = () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && params.append(k, v));
    window.location.href = `${API}/audit/transactions.csv?${params.toString()}`;
  };

  return (
    <div className="min-h-screen bg-[#FAFAF6] text-[#0A0A0A]" data-testid="audit-dashboard">
      {/* Header */}
      <div className="border-b border-[#E5E5DF] bg-white sticky top-0 z-10">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="btn-ghost" data-testid="audit-back-btn">
              <ArrowLeft size={14} /> Workbench
            </Link>
            <div>
              <h1 className="font-serif-display text-[22px] leading-tight">Audit Dashboard</h1>
              <div className="text-[10px] font-mono-fin uppercase tracking-[0.16em] text-[#5C5C5C]">
                Production transaction log · MongoDB-backed · TTL-365d
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <button className="btn-ghost" onClick={refresh} data-testid="audit-refresh-btn">
              <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button className="btn-secondary" onClick={downloadCSV} data-testid="audit-csv-btn">
              <Download size={13} /> Export CSV
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 py-8 space-y-8">
        {/* Aggregate cards */}
        {summary && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="audit-summary-cards">
            <SummaryCard label="Total Transactions" value={summary.total_transactions} accent="#0B4636" testid="summary-total" />
            <SummaryCard label="Total Face Notional" value={fmt$(summary.totals.face)} accent="#102968" testid="summary-face" />
            <SummaryCard label="Total Discount Volume" value={fmt$(summary.totals.discount)} accent="#9E2A2B" testid="summary-discount" />
            <SummaryCard label="Distinct Statuses" value={Object.keys(summary.by_status || {}).length} accent="#5C5C5C" testid="summary-statuses" />
          </div>
        )}

        {/* Status + Path breakdown */}
        {summary && (
          <div className="grid lg:grid-cols-2 gap-4">
            <div className="bg-white border border-[#E5E5DF] rounded-sm p-5">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] text-[#5C5C5C] mb-3">
                <BarChart3 size={12} /> Status Distribution
              </div>
              <div className="space-y-2" data-testid="audit-status-breakdown">
                {Object.entries(summary.by_status || {}).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between text-[12.5px]">
                    <span className={`text-[10px] uppercase tracking-[0.14em] font-bold px-2 py-0.5 rounded-sm border ${STATUS_COLORS[k] || ""}`}>{k}</span>
                    <span className="font-mono-fin">{v}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white border border-[#E5E5DF] rounded-sm p-5">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] text-[#5C5C5C] mb-3">
                <BarChart3 size={12} /> Path Breakdown
              </div>
              <div className="space-y-2" data-testid="audit-path-breakdown">
                {Object.entries(summary.by_path || {}).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between text-[12.5px]">
                    <span className="font-medium">{PATH_LABEL[k] || k}</span>
                    <span className="font-mono-fin text-[#5C5C5C]">
                      {v.count} deals · {fmt$(v.face_total)} face
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Filter bar */}
        <div className="bg-white border border-[#E5E5DF] rounded-sm p-4 flex flex-wrap gap-3 items-end">
          <div className="text-[10px] uppercase tracking-[0.16em] text-[#5C5C5C] flex items-center gap-1">
            <Filter size={12} /> Filter
          </div>
          <select
            className="border border-[#E5E5DF] rounded-sm px-2 py-1 text-[12px]"
            value={filters.status}
            onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
            data-testid="audit-filter-status"
          >
            <option value="">All statuses</option>
            <option value="open">Open</option>
            <option value="issued">Issued</option>
            <option value="closing">Closing</option>
            <option value="closed">Closed</option>
            <option value="matured">Matured</option>
          </select>
          <select
            className="border border-[#E5E5DF] rounded-sm px-2 py-1 text-[12px]"
            value={filters.path}
            onChange={(e) => setFilters((f) => ({ ...f, path: e.target.value }))}
            data-testid="audit-filter-path"
          >
            <option value="">All paths</option>
            <option value="spv_trust">SPV Trust</option>
            <option value="discount_sale">Outright Sale</option>
            <option value="financing">Collateral Loan</option>
          </select>
          <input
            type="date"
            className="border border-[#E5E5DF] rounded-sm px-2 py-1 text-[12px] hidden"
            value={filters.since}
            onChange={(e) => setFilters((f) => ({ ...f, since: e.target.value }))}
            data-testid="audit-filter-since"
          />
          <DateField
            label="Since"
            value={filters.since}
            onChange={(v) => setFilters((f) => ({ ...f, since: v }))}
            testid="audit-filter-since-popover"
          />
          <input
            type="date"
            className="border border-[#E5E5DF] rounded-sm px-2 py-1 text-[12px] hidden"
            value={filters.until}
            onChange={(e) => setFilters((f) => ({ ...f, until: e.target.value }))}
            data-testid="audit-filter-until"
          />
          <DateField
            label="Until"
            value={filters.until}
            onChange={(v) => setFilters((f) => ({ ...f, until: v }))}
            testid="audit-filter-until-popover"
          />
          <button className="btn-secondary" onClick={refresh} data-testid="audit-apply-filters">Apply</button>
        </div>

        {/* Transactions table */}
        <div className="bg-white border border-[#E5E5DF] rounded-sm overflow-hidden" data-testid="audit-table">
          <div className="px-4 py-3 border-b border-[#E5E5DF] text-[10px] uppercase tracking-[0.16em] text-[#5C5C5C] flex justify-between">
            <span>Transactions · {txns.length} on record</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead>
                <tr className="border-b border-[#E5E5DF] text-[10.5px] uppercase tracking-[0.12em] text-[#5C5C5C]">
                  <th className="text-left px-4 py-2.5">Status</th>
                  <th className="text-left px-3 py-2.5">Path</th>
                  <th className="text-left px-3 py-2.5">Trust / Maker</th>
                  <th className="text-left px-3 py-2.5">Counterparty</th>
                  <th className="text-right px-3 py-2.5">Face</th>
                  <th className="text-right px-3 py-2.5">Sale</th>
                  <th className="text-right px-3 py-2.5">Events</th>
                  <th className="text-left px-3 py-2.5">Created</th>
                </tr>
              </thead>
              <tbody>
                {txns.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center py-8 text-[#8A8A8A] text-[12px]" data-testid="audit-table-empty">
                      No transactions match the current filter.
                    </td>
                  </tr>
                )}
                {txns.map((tx) => (
                  <tr
                    key={tx.id}
                    onClick={() => setDrillDown({ tx, log: null, loading: true })}
                    className={`border-b border-[#E5E5DF] hover:bg-[#FAFAF6] cursor-pointer ${drillDown?.tx?.id === tx.id ? "bg-[#FAFAF6]" : ""}`}
                    data-testid={`audit-row-${tx.id}`}
                  >
                    <td className="px-4 py-2">
                      <span className={`text-[10px] uppercase tracking-[0.14em] font-bold px-2 py-0.5 rounded-sm border ${STATUS_COLORS[tx.status] || ""}`}>
                        {tx.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-[11px]">{PATH_LABEL[tx.path] || tx.path}</td>
                    <td className="px-3 py-2 font-medium">{tx.trust_name}</td>
                    <td className="px-3 py-2 text-[#5C5C5C]">{tx.counterparty_name}</td>
                    <td className="px-3 py-2 text-right font-mono-fin">{fmt$(tx.face_value)}</td>
                    <td className="px-3 py-2 text-right font-mono-fin">{fmt$(tx.purchase_price)}</td>
                    <td className="px-3 py-2 text-right font-mono-fin text-[#5C5C5C]">{(tx.events || []).length}</td>
                    <td className="px-3 py-2 text-[10.5px] font-mono-fin text-[#8A8A8A]">{fmtTs(tx.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Drill-down: when a transaction row is clicked, show its event_log */}
        {drillDown?.tx && (
          <div className="bg-white border-2 border-[#0B4636] rounded-sm p-5" data-testid="audit-drilldown">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-[#0B4636] font-bold">
                  Event Log · {drillDown.tx.trust_name}
                </div>
                <div className="text-[10.5px] font-mono-fin text-[#5C5C5C] mt-0.5">tx {drillDown.tx.id}</div>
              </div>
              <button
                onClick={() => setDrillDown(null)}
                className="btn-ghost"
                data-testid="audit-drilldown-close"
              >
                <X size={13} /> Close
              </button>
            </div>
            {drillDown.loading && (
              <div className="text-center py-6 text-[#8A8A8A] text-[12px]">Loading event log…</div>
            )}
            {drillDown.log && !drillDown.loading && (
              <>
                <div className="text-[11px] text-[#5C5C5C] mb-3">
                  {drillDown.log.count} webhook-fanout entries persisted in <span className="font-mono-fin">db.event_log</span>.
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-[11.5px]">
                    <thead>
                      <tr className="border-b border-[#E5E5DF] text-[10px] uppercase tracking-[0.12em] text-[#5C5C5C]">
                        <th className="text-left px-3 py-2">Event Type</th>
                        <th className="text-left px-3 py-2">Source</th>
                        <th className="text-left px-3 py-2">Dispatched</th>
                        <th className="text-right px-3 py-2">Sig (sha256)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {drillDown.log.entries?.length === 0 && (
                        <tr><td colSpan={4} className="text-center py-4 text-[#8A8A8A]">
                          No webhook fan-outs yet for this transaction.
                        </td></tr>
                      )}
                      {drillDown.log.entries?.map((ev) => (
                        <tr key={ev.id} className="border-b border-[#E5E5DF]/60">
                          <td className="px-3 py-2 font-mono-fin font-semibold text-[#0B4636]">{ev.event_type}</td>
                          <td className="px-3 py-2 font-mono-fin text-[#5C5C5C]">{ev.source}</td>
                          <td className="px-3 py-2 font-mono-fin text-[10.5px]">{fmtTs(ev.dispatched_at)}</td>
                          <td className="px-3 py-2 font-mono-fin text-[9.5px] text-right text-[#8A8A8A] truncate max-w-[200px]">
                            {(ev.payload?.signature || "—").slice(0, 16)}…
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {/* Recent events */}
        {summary?.recent_events?.length > 0 && (
          <div className="bg-white border border-[#E5E5DF] rounded-sm p-5">
            <div className="text-[10px] uppercase tracking-[0.16em] text-[#5C5C5C] mb-3">
              Last 10 Events Across All Transactions
            </div>
            <div className="space-y-2" data-testid="audit-recent-events">
              {summary.recent_events.map((ev) => (
                <div key={ev.id} className="flex items-center gap-3 text-[11.5px] border-l-2 border-[#0B4636] pl-3 py-1">
                  <span className="font-mono-fin text-[#0B4636] font-semibold">{ev.event_type}</span>
                  <span className="text-[#8A8A8A]">{fmtTs(ev.dispatched_at)}</span>
                  {ev.transaction_id && (
                    <span className="font-mono-fin text-[10px] text-[#5C5C5C]">
                      tx={ev.transaction_id.slice(0, 8)}…
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryCard({ label, value, accent, testid }) {
  return (
    <div className="bg-white border border-[#E5E5DF] rounded-sm p-5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.16em] text-[#5C5C5C] mb-2">{label}</div>
      <div className="font-mono-fin font-semibold text-[26px]" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function DateField({ label, value, onChange, testid }) {
  const date = value ? new Date(value) : undefined;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="border border-[#E5E5DF] rounded-sm px-3 py-1 text-[12px] flex items-center gap-1.5 hover:border-[#0B4636] min-w-[140px]"
          data-testid={testid}
        >
          <CalendarIcon size={12} className="text-[#5C5C5C]" />
          <span className="text-[#5C5C5C]">{label}:</span>
          <span className="font-mono-fin">{value || "—"}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={date}
          onSelect={(d) => {
            if (!d) { onChange(""); return; }
            const iso = d.toISOString().slice(0, 10);
            onChange(iso);
          }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}
